import React, { useCallback, useMemo, useState } from "react";
import { makeRemoteNbsIbscbsAdmin } from "../../../usecases/nbs-ibscbs/remote-nbs-ibscbs-admin-factory";
import {
  NbsIbscbsListModel,
  NbsSearchResult,
} from "../../../../../domain/models/nbs-ibscbs";
import NbsIbscbsAdminPage from "../../../../../presentation/pages/admin/nbs-ibscbs";
import toastUtils from "../../../../../utils/ToastUtils";

const MakeNbsIbscbsAdmin: React.FC = () => {
  const service = useMemo(() => makeRemoteNbsIbscbsAdmin(), []);

  // Busca de NBS
  const [nbsSearch, setNbsSearch] = useState("");
  const [nbsResults, setNbsResults] = useState<NbsSearchResult[]>([]);
  const [nbsLoading, setNbsLoading] = useState(false);

  // NBS selecionada
  const [selectedNbs, setSelectedNbs] = useState<NbsSearchResult | null>(null);
  const [nbsIbscbsData, setNbsIbscbsData] = useState<NbsIbscbsListModel | null>(null);
  const [combinationsLoading, setCombinationsLoading] = useState(false);

  // Ações
  const [settingDefault, setSettingDefault] = useState(false);

  // --- Debounce para busca ---
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleNbsSearchChange = useCallback((search: string): void => {
    setNbsSearch(search);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (search.length < 2) {
      setNbsResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setNbsLoading(true);
        const results = await service.searchNbs(search);
        setNbsResults(results);
      } catch {
        toastUtils.showMessage({ type: "error", message: "Erro ao buscar NBS." });
      } finally {
        setNbsLoading(false);
      }
    }, 400);
  }, [service]);

  // --- Selecionar NBS ---
  const handleSelectNbs = useCallback(async (nbs: NbsSearchResult): Promise<void> => {
    setSelectedNbs(nbs);
    setCombinationsLoading(true);

    try {
      const data = await service.listByNbsId(nbs.id);
      setNbsIbscbsData(data);
    } catch {
      setNbsIbscbsData(null);
      toastUtils.showMessage({ type: "error", message: "Erro ao carregar combinações IBSCBS." });
    } finally {
      setCombinationsLoading(false);
    }
  }, [service]);

  // --- Voltar ---
  const handleBack = useCallback((): void => {
    setSelectedNbs(null);
    setNbsIbscbsData(null);
  }, []);

  // --- Trocar default ---
  const handleSetDefault = useCallback(async (id: number): Promise<void> => {
    if (!selectedNbs) return;

    try {
      setSettingDefault(true);
      await service.setDefault(id);
      toastUtils.showMessage({ type: "success", message: "Default alterado com sucesso!" });

      // Recarrega as combinações
      const data = await service.listByNbsId(selectedNbs.id);
      setNbsIbscbsData(data);
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao alterar default." });
    } finally {
      setSettingDefault(false);
    }
  }, [service, selectedNbs]);

  return (
    <NbsIbscbsAdminPage
      nbsSearch={nbsSearch}
      nbsResults={nbsResults}
      nbsLoading={nbsLoading}
      onNbsSearchChange={handleNbsSearchChange}
      onSelectNbs={handleSelectNbs}
      selectedNbs={selectedNbs}
      nbsIbscbsData={nbsIbscbsData}
      combinationsLoading={combinationsLoading}
      onBack={handleBack}
      onSetDefault={handleSetDefault}
      settingDefault={settingDefault}
    />
  );
};

export default MakeNbsIbscbsAdmin;
